import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import { SkeletonTable } from '../../components/SkeletonTable';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmModal } from '../../components/ConfirmModal';
import { TenantOnboardingWizard } from '../../components/wizards/TenantOnboardingWizard';

export function AdminTenantsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<'suspend' | 'activate' | null>(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const { data: tenants, isLoading } = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => adminApi.getTenants(),
  });

  const { data: pricingPlans } = useQuery({
    queryKey: ['admin', 'pricingPlans'],
    queryFn: () => adminApi.getPricingPlans(),
    enabled: showCreateModal,
  });

  const createTenantMutation = useMutation({
    mutationFn: (data: { name: string; email: string; pricingPlanId: string }) =>
      adminApi.createTenant(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      setShowCreateModal(false);
      addToast({ type: 'success', message: 'Tenant created successfully' });
    },
    onError: (error) => {
      addToast({ type: 'error', message: `Failed to create tenant: ${error instanceof Error ? error.message : 'Unknown error'}` });
    },
  });

  const handleCreateTenant = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    createTenantMutation.mutate({
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      pricingPlanId: formData.get('pricingPlanId') as string,
    });
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!tenants) return;
    if (selectedIds.size === tenants.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(tenants.map((t) => t.tenantId)));
    }
  };

  const executeBulkAction = async () => {
    if (!bulkAction) return;
    const ids = Array.from(selectedIds);
    const newStatus = bulkAction === 'suspend' ? 'suspended' : 'active';
    setBulkProcessing(true);

    const results = await Promise.allSettled(
      ids.map((id) => adminApi.updateTenant(id, { status: newStatus }))
    );

    const failures = results.filter((r) => r.status === 'rejected').length;
    const successes = results.length - failures;

    if (failures === 0) {
      addToast({ type: 'success', message: `${successes} tenant(s) ${newStatus}` });
    } else {
      addToast({ type: 'warning', message: `${successes} succeeded, ${failures} failed` });
    }

    queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    setSelectedIds(new Set());
    setBulkAction(null);
    setBulkProcessing(false);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="h-8 w-32 bg-gray-200 rounded animate-pulse" />
          <div className="h-10 w-36 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="card"><SkeletonTable rows={8} columns={5} /></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Tenants</h1>
        <div className="flex gap-2">
          <button
            className="btn btn-secondary text-sm"
            onClick={() => setShowOnboardingWizard(true)}
          >
            🧙 Guided Setup
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            + Create Tenant
          </button>
        </div>
      </div>

      {/* Tenant List */}
      {tenants && tenants.length > 0 ? (
        <div className="card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="py-2 px-4 w-8">
                  <input
                    type="checkbox"
                    checked={tenants.length > 0 && selectedIds.size === tenants.length}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="text-left py-2 px-4">Name</th>
                <th className="text-left py-2 px-4">Email</th>
                <th className="text-left py-2 px-4">Plan</th>
                <th className="text-left py-2 px-4">Status</th>
                <th className="text-left py-2 px-4">Created</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => (
                <tr
                  key={tenant.tenantId}
                  className="border-b cursor-pointer hover:bg-gray-50"
                  onClick={() => navigate(`/admin/tenants/${tenant.tenantId}`)}
                >
                  <td className="py-2 px-4" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(tenant.tenantId)}
                      onChange={() => toggleSelect(tenant.tenantId)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="py-2 px-4 font-medium text-primary-600 hover:underline">{tenant.name}</td>
                  <td className="py-2 px-4">{tenant.email}</td>
                  <td className="py-2 px-4 text-gray-500">{tenant.pricingPlanId}</td>
                  <td className="py-2 px-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      tenant.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : tenant.status === 'suspended'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {tenant.status}
                    </span>
                  </td>
                  <td className="py-2 px-4 text-gray-500">
                    {new Date(tenant.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card">
          <EmptyState
            icon="👤"
            title="No tenants yet"
            description="Create your first tenant to get started"
            actionLabel="+ Create Tenant"
            onAction={() => setShowCreateModal(true)}
          />
        </div>
      )}

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white py-3 px-6 flex items-center justify-between z-40">
          <span>{selectedIds.size} tenant{selectedIds.size > 1 ? 's' : ''} selected</span>
          <div className="flex gap-3">
            <button
              className="px-3 py-1.5 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
              onClick={() => setBulkAction('suspend')}
            >
              Suspend Selected
            </button>
            <button
              className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700"
              onClick={() => setBulkAction('activate')}
            >
              Activate Selected
            </button>
            <button
              className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Bulk Action Confirmation */}
      {bulkAction && (
        <ConfirmModal
          title={`${bulkAction === 'suspend' ? 'Suspend' : 'Activate'} ${selectedIds.size} Tenant(s)?`}
          message={bulkAction === 'suspend'
            ? 'This will disable API access for the selected tenants.'
            : 'This will re-enable API access for the selected tenants.'
          }
          confirmLabel={`${bulkAction === 'suspend' ? 'Suspend' : 'Activate'} (${selectedIds.size})`}
          confirmVariant={bulkAction === 'suspend' ? 'danger' : 'primary'}
          isLoading={bulkProcessing}
          onConfirm={executeBulkAction}
          onCancel={() => setBulkAction(null)}
        />
      )}

      {/* Create Tenant Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Create Tenant</h2>
            <form onSubmit={handleCreateTenant} className="space-y-4">
              <div>
                <label className="label">Name</label>
                <input name="name" className="input" required />
              </div>
              <div>
                <label className="label">Email</label>
                <input name="email" type="email" className="input" required />
              </div>
              <div>
                <label className="label">Pricing Plan</label>
                <select name="pricingPlanId" className="input" required>
                  <option value="">Select a plan...</option>
                  {pricingPlans?.map(p => (
                    <option key={p.planId} value={p.planId}>
                      {p.name} — {p.planType === 'per-label' ? `$${p.perLabelRate}/label` : `$${p.monthlyFlatRate}/mo`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end space-x-3">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={createTenantMutation.isPending}>
                  {createTenantMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tenant Onboarding Wizard */}
      {showOnboardingWizard && (
        <TenantOnboardingWizard onClose={() => setShowOnboardingWizard(false)} />
      )}
    </div>
  );
}
